import { useState, useEffect } from 'react';
import { Search, Button, Loading } from 'carbon-components-react';

import { Container, ContentContainer } from '../../components/Wrapper/Wrapper';

import TileHeader from '../../components/TileHeader/TileHeader';
import ConfirmModal from '../../components/Modal/ConfirmModal';
import GlossaryGrid from './GlossaryGrid';
import GlossaryDetailModal from './GlossaryDetailModal';

import { getAllGlossaryTerms, deleteGlossaryTerm } from '../../api/glossary';

import { toaster } from '../../helpers/toaster';
import usePollReport from '../../hooks/usePollReport';
import { triggerReport } from '../../api/studyListApi';

import './Glossary.scss';

const Glossary = () => {
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [searchGrid, setSearchGrid] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState("");
  const [refreshData, setRefreshData] = useState(false);
  const [sort, setSort] = useState("");

  // modal params
  const [detailModalMode, setDetailModalMode] = useState("");
  const [termId, setTermId] = useState(null);
  const [modalTerm, setModalTerm] = useState("");
  const [modalDefinition, setModalDefinition] = useState("");
  const [modalSupportingInfo, setModalSupportingInfo] = useState("");

  const { startPolling, status: reportStatus } = usePollReport({
    onSuccess: () => { toaster('Glossary report downloaded successfully', 'success'); },
    onError: () => toaster('Failed to generate report. Please try again.', 'error'),
  });

  const [openReportModal, setOpenReportModal] = useState(false);

  const handleGenerateReport = () => {
    setOpenReportModal(false);
    triggerReport('glossary')
      .then(({ task_id }) => {
        toaster('Generating glossary report...', 'info');
        startPolling(task_id);
      })
      .catch(() => toaster('Failed to start report generation.', 'error'));
  };

  const btnStyle = {
    padding: "11px 18px",
    height: "auto",
    minHeight: "unset",
    borderRadius: "10px",
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "13.5px",
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchGrid(search);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [search]);

  const addNewTerm = () => {
    setDetailModalMode("create");
    setModalTerm("");
    setModalDefinition("");
    setModalSupportingInfo("");
    setTermId(null);
    setOpenModal(true);
  };

  const termRowClicked = (data) => {
    setDetailModalMode("update");
    setTermId(data.id);
    setModalTerm(data.term || "");
    setModalDefinition(data.definition || "");
    setModalSupportingInfo(data.supporting_information || "");
    setOpenModal(true);
  };

  const showDeleteConfirm = (itemId, itemName) => {
    setOpenDeleteModal(true);
    setDeleteItemId(itemId);
    setDeleteItemName(itemName || "");
  };

  const confirmDelete = () => {
    setOpenDeleteModal(false);
    setLoading(true);
    deleteGlossaryTerm(deleteItemId).then(res => {
      toaster("Term deleted successfully", "success");
      setLoading(false);
      setRefreshData(true);
    });
  };

  const refreshTriggered = () => {
    setRefreshData(true);
  };

  return (
    <Container>
      <ContentContainer>

        <TileHeader
          title="Glossary"
          size="lg"
        />
        {loading && <Loading />}

        <div className="glossary-content">
          <div className="glossary-action-bar">
            <div style={{ width: "300px" }}>
              <Search
                size="sm"
                value={search}
                labelText="search"
                onChange={e => setSearch(e.target.value)}
                placeHolderText="Search using any data value"
              />
            </div>

            <div className="glossary-button-group">
              <Button
                kind="secondary"
                onClick={() => setOpenReportModal(true)}
                disabled={reportStatus === 'polling'}
                style={btnStyle}
              >{reportStatus === 'polling' ? 'Generating...' : 'Generate Report'}</Button>
              <Button
                kind="primary"
                onClick={() => addNewTerm()}
                style={btnStyle}
              >Add Term</Button>
            </div>
          </div>

          <GlossaryGrid
            search={searchGrid}
            onRowClicked={termRowClicked}
            onDeleteClicked={showDeleteConfirm}
            setRefresh={setRefreshData}
            refresh={refreshData}
            sort={sort}
            setSort={setSort}
          />
        </div>

        <GlossaryDetailModal
          mode={detailModalMode}
          termId={termId}
          open={openModal}
          term={modalTerm}
          definition={modalDefinition}
          supportingInformation={modalSupportingInfo}
          refreshTerms={refreshTriggered}
          onClose={() => setOpenModal(false)}
        />

        <ConfirmModal
          title="Delete Term"
          content={`Are you sure you want to delete the term "${deleteItemName}"? It will be removed from the glossary on the website.`}
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={() => confirmDelete()}
        />
        <ConfirmModal
          title="Generate Report"
          content="Do you want to generate the report? This may take awhile. Navigating away from this page will stop the report generation."
          open={openReportModal}
          onClose={() => setOpenReportModal(false)}
          onConfirm={handleGenerateReport}
        />

      </ContentContainer>
    </Container>
  );
};

export default Glossary;
